/**
 * T068: useUpdatePrompt Hook
 * Mutation hook for updating prompts with optimistic updates
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { updatePrompt, type UpdatePromptInput, type PromptResponse } from '@/services/prompts'
import { queryKeys } from '@/lib/query-keys'
import { ApiError } from '@/services/api'

interface UpdatePromptVariables {
  id: string
  input: UpdatePromptInput
}

export function useUpdatePrompt() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, input }: UpdatePromptVariables) => updatePrompt(id, input),
    onMutate: async ({ id, input }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.prompts.detail(id) })

      // Snapshot previous value
      const previousPrompt = queryClient.getQueryData<PromptResponse>(
        queryKeys.prompts.detail(id)
      )

      // Optimistically update
      if (previousPrompt) {
        queryClient.setQueryData<PromptResponse>(queryKeys.prompts.detail(id), {
          ...previousPrompt,
          ...input,
          title: input.title ?? previousPrompt.title,
          content: input.content ?? previousPrompt.content,
        })
      }

      return { previousPrompt }
    },
    onError: (error, { id }, context) => {
      // Rollback on error
      if (context?.previousPrompt) {
        queryClient.setQueryData(queryKeys.prompts.detail(id), context.previousPrompt)
      }

      // Handle 409 conflict error
      if (error instanceof ApiError && error.status === 409) {
        // The caller should handle this with a dialog
        console.warn('Conflict detected - prompt was modified by another user')
      }
    },
    onSettled: (_, __, { id }) => {
      // Always refetch after mutation
      queryClient.invalidateQueries({ queryKey: queryKeys.prompts.detail(id) })
      queryClient.invalidateQueries({ queryKey: queryKeys.prompts.all })
    },
  })
}
